// src/components/GameCard.tsx

import React, {
  useState,
} from "react";

import FasoorModal from "./FasoorModal";

import "./GameCard.css";

// =====================================================
// TYPES
// =====================================================

interface GameRating {
  averageRating: number;
  ratingCount: number;
}

interface Game {
  id: number;
  name: string;
  image: string;

  /*
    Controls whether the game can currently be played.

    This prevents GameCard from being hard-coded
    specifically for Fasoor.
  */
  available: boolean;

  /*
    Community rating for this particular game.

    Examples:
      fasoor
      tika
      bitruno
      charwali
      fiskoot

    Home.tsx will load this information from
    the backend rating API.
  */
  rating?: GameRating;
}

interface GameCardProps {
  game: Game;
  onClick?: () => void;
}

// =====================================================
// GAME CARD
// =====================================================

const GameCard: React.FC<GameCardProps> = ({
  game,
  onClick,
}) => {

  const [
    showComingSoon,
    setShowComingSoon,
  ] = useState(false);

  // ===================================================
  // DISPLAY NAME
  // ===================================================

  const displayName =
    game.name.replace(
      " - Coming Soon!",
      ""
    );

  // ===================================================
  // GAME CLICK
  // ===================================================

  const handleClick = () => {

    /*
      IMPORTANT:

      Do not hard-code Fasoor here.

      Every game uses the same availability system.

      When Tika, Bitruno, Charwali, Fiskoot,
      or another future game becomes available,
      Home.tsx only needs:

        available: true
    */

    if (!game.available) {

      setShowComingSoon(
        true
      );

      return;
    }

    /*
      Home.tsx handles:

      - P-ARENA splash
      - Selected game
      - Lobby navigation

      Guest access is allowed because players
      may play against AI.
    */

    onClick?.();
  };

  // ===================================================
  // KEYBOARD
  // ===================================================

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {

    if (
      event.key === "Enter" ||
      event.key === " "
    ) {

      event.preventDefault();

      handleClick();
    }
  };

  // ===================================================
  // CLOSE COMING SOON
  // ===================================================

  const closeComingSoon = () => {

    setShowComingSoon(
      false
    );
  };

  // ===================================================
  // RATING
  // ===================================================

  const hasRating =
    Boolean(
      game.rating &&
      game.rating.ratingCount > 0
    );

  // ===================================================
  // UI
  // ===================================================

  return (
    <>

      <div
        className={
          `game-card ${
            game.available
              ? "game-card--available"
              : "game-card--coming-soon"
          }`
        }
        onClick={
          handleClick
        }
        role="button"
        tabIndex={0}
        onKeyDown={
          handleKeyDown
        }
        aria-label={
          game.available
            ? `Play ${displayName}`
            : `${displayName} coming soon`
        }
      >

        {/* ========================================= */}
        {/* GAME IMAGE                                */}
        {/* ========================================= */}

        <div className="game-image-wrapper">

          <img
            src={
              game.image
            }
            alt={
              displayName
            }
            draggable={
              false
            }
          />

          {/* ======================================= */}
          {/* PLAY / COMING SOON OVERLAY              */}
          {/* ======================================= */}

          <div className="play-overlay">

            {game.available
              ? "▶ Play"
              : "Coming Soon"}

          </div>

        </div>


        {/* ========================================= */}
        {/* GAME COMMUNITY RATING                    */}
        {/* ========================================= */}

        {game.available && (

          <div
            className="game-card-rating"
            aria-label={
              hasRating
                ? `${game.rating!.averageRating.toFixed(
                    1
                  )} out of 5 from ${
                    game.rating!.ratingCount
                  } ratings`
                : `${displayName} has no ratings yet`
            }
          >

            {hasRating ? (

              <>

                {/* STAR */}

                <span
                  className="game-card-rating-star"
                  aria-hidden="true"
                >
                  ★
                </span>


                {/* AVERAGE */}

                <strong className="game-card-rating-score">

                  {
                    game.rating!.averageRating.toFixed(
                      1
                    )
                  }

                </strong>


                {/* DIVIDER */}

                <span
                  className="game-card-rating-divider"
                  aria-hidden="true"
                >
                  |
                </span>


                {/* RATING COUNT */}

                <span className="game-card-rating-count">

                  {
                    game.rating!.ratingCount.toLocaleString()
                  }

                  {" "}

                  {
                    game.rating!.ratingCount === 1
                      ? "rating"
                      : "ratings"
                  }

                </span>

              </>

            ) : (

              <span className="game-card-rating-empty">

                <span
                  aria-hidden="true"
                  className="game-card-rating-empty-star"
                >
                  ☆
                </span>

                No ratings yet

              </span>

            )}

          </div>

        )}

      </div>


      {/* =========================================== */}
      {/* COMING SOON MODAL                           */}
      {/* =========================================== */}

      <FasoorModal
        open={
          showComingSoon
        }
        type="game"
        title="Coming Soon"
        message={
          `${displayName} is currently under development.`
        }
        description={
          `${displayName} is coming to P-ARENA. Check back soon for availability.`
        }
        confirmText="Got It"
        onConfirm={
          closeComingSoon
        }
        onCancel={
          closeComingSoon
        }
      />

    </>
  );
};

export default GameCard;